import type {
  CaptureManifest,
  DebugSurfaceContext,
  PendingBugRequest,
} from "./types";
import { getCaptureZipFilename } from "./bundle";
import { formatSeverityLabel } from "./clickup-bug-fields";
import { formatMeaningfulComponentStack } from "./component-stack";

const MODULE_MAP: Record<string, string> = {
  academies: "Academies",
  "performance-plus": "Performance Plus",
  "assets-rental": "Assets Rental",
  gym: "Gym",
  marketing: "Marketing",
  "access-control": "Access Control",
  "customer-support": "Customer Support",
  dining: "Dining",
  login: "Auth",
};

export function deriveModuleFromPathname(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const feature = segments[1];

  if (!feature) {
    return "General";
  }

  return MODULE_MAP[feature] ?? feature.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Human-readable page route for the bug form — not the technical capture summary. */
export function formatModalStep(
  surface: DebugSurfaceContext | undefined
): string | undefined {
  if (!surface) {
    return undefined;
  }

  if (surface.stepLabel) {
    const stepNumber =
      surface.stepIndex != null ? surface.stepIndex + 1 : undefined;

    if (stepNumber && surface.stepCount) {
      return `Step ${stepNumber}/${surface.stepCount}: ${surface.stepLabel}`;
    }

    return surface.stepLabel;
  }

  if (surface.mode === "edit") {
    return "Edit";
  }

  return undefined;
}

function formatSurfaceRouteHint(
  surface: CaptureManifest["surface"]
): string {
  if (!surface) {
    return "";
  }

  const parts: string[] = [];

  if (surface.title) {
    parts.push(surface.title);
  }

  const step = formatModalStep(surface);
  if (step) {
    parts.push(step);
  }

  if (surface.itemLabel) {
    parts.push(surface.itemLabel);
  }

  return parts.length > 0 ? ` · Modal: ${parts.join(" · ")}` : "";
}

function formatPageEntityHint(
  pageEntity: CaptureManifest["pageEntity"]
): string {
  if (!pageEntity) {
    return "";
  }

  return ` · ${pageEntity.type}: ${pageEntity.id}`;
}

export function buildHumanRouteLabel(
  manifest: Pick<
    CaptureManifest,
    "pathname" | "context" | "pageEntity" | "surface"
  >
): string {
  const moduleLabel = deriveModuleFromPathname(manifest.pathname);
  const entityHint = formatPageEntityHint(manifest.pageEntity);
  const surfaceHint = formatSurfaceRouteHint(manifest.surface);
  const modalHint =
    !manifest.surface && manifest.context.isModal
      ? ` · ${manifest.context.isDrawer ? "Drawer" : "Modal"}${manifest.context.modalTitle ? `: ${manifest.context.modalTitle}` : ""}`
      : "";

  if (moduleLabel !== "General") {
    return `${moduleLabel} · ${manifest.pathname}${entityHint}${surfaceHint || modalHint}`;
  }

  return `${manifest.pathname}${entityHint}${surfaceHint || modalHint}`;
}

export function deriveBranchLabel(
  branchId: string | undefined,
  pathname: string
): string {
  const key = branchId ?? pathname.split("/").filter(Boolean)[0] ?? "";

  if (/^branch_([a-z])$/i.test(key)) {
    return `Branch ${key.replace(/^branch_/, "").toUpperCase()}`;
  }

  if (key) {
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return "Unknown";
}

export function buildDefaultPendingBug(
  manifest: Pick<
    CaptureManifest,
    "pathname" | "branchId" | "appVersion" | "context" | "pageEntity" | "surface"
  >
): PendingBugRequest {
  const surface = manifest.surface;

  return {
    title: "",
    route: buildHumanRouteLabel(manifest),
    pageEntityId: manifest.pageEntity?.id,
    pageEntityType: manifest.pageEntity?.type,
    modalTitle: surface?.title,
    modalMode: surface?.mode,
    modalStep: formatModalStep(surface),
    modalEntityId: surface?.entityId,
    modalEntityType: surface?.entityType,
    modalItemLabel: surface?.itemLabel,
    module: surface?.module ?? deriveModuleFromPathname(manifest.pathname),
    project: "Admin Portal",
    branch: deriveBranchLabel(manifest.branchId, manifest.pathname),
    appVersion: manifest.appVersion,
    description: "",
    stepsToReproduce: "",
    otherNotes: "",
    expectedResults: "",
    actualResults: "",
    reportedBy: "",
    severity: "3",
    priority: "normal",
    tags: "v2 bug",
    assigneeName: "",
    assigneeEmail: "",
  };
}

export function buildClickUpBugDescription(
  pendingBug: PendingBugRequest,
  manifest: CaptureManifest
): string {
  const stack = formatMeaningfulComponentStack(manifest.react.componentStack);
  const primary = manifest.react.primarySource;
  const primaryHint = primary
    ? `${primary.fileName.replace(/.*[/\\]src[/\\]/, "src/")}:${primary.lineNumber}`
    : "n/a";

  return [
    `Module:\n${pendingBug.module ?? deriveModuleFromPathname(manifest.pathname)}`,
    "",
    `Project:\n${pendingBug.project ?? "Admin Portal"}`,
    "",
    `Branch:\n${pendingBug.branch ?? deriveBranchLabel(manifest.branchId, manifest.pathname)}`,
    "",
    `App Version:\n${pendingBug.appVersion ?? manifest.appVersion ?? "unknown"}`,
    "",
    `Route:\n${pendingBug.route?.trim() || buildHumanRouteLabel(manifest)}`,
    "",
    ...(pendingBug.pageEntityId?.trim() || manifest.pageEntity?.id
      ? [
          `Record UUID:\n${pendingBug.pageEntityId?.trim() || manifest.pageEntity?.id}`,
          `Record type:\n${pendingBug.pageEntityType?.trim() || manifest.pageEntity?.type || "Entity"}`,
          "",
        ]
      : []),
    ...(pendingBug.modalTitle?.trim() ||
    manifest.surface?.title ||
    pendingBug.modalEntityId?.trim() ||
    manifest.surface?.entityId
      ? [
          `Modal:\n${pendingBug.modalTitle?.trim() || manifest.surface?.title || "—"}`,
          `Modal mode:\n${pendingBug.modalMode?.trim() || manifest.surface?.mode || "—"}`,
          `Modal step:\n${pendingBug.modalStep?.trim() || formatModalStep(manifest.surface) || "—"}`,
          `Modal record:\n${pendingBug.modalEntityType?.trim() || manifest.surface?.entityType || "—"} (${pendingBug.modalEntityId?.trim() || manifest.surface?.entityId || "—"})`,
          ...(pendingBug.modalItemLabel?.trim() || manifest.surface?.itemLabel
            ? [
                `Modal item:\n${pendingBug.modalItemLabel?.trim() || manifest.surface?.itemLabel}`,
              ]
            : []),
          "",
        ]
      : []),
    `Description:\n${pendingBug.description?.trim() || pendingBug.title}`,
    "",
    `Steps to reproduce:\n${pendingBug.stepsToReproduce?.trim() || "—"}`,
    "",
    ...(pendingBug.otherNotes?.trim()
      ? [`Other:\n${pendingBug.otherNotes.trim()}`, ""]
      : []),
    `Expected Results:\n${pendingBug.expectedResults?.trim() || "—"}`,
    "",
    `Actual Results:\n${pendingBug.actualResults?.trim() || "—"}`,
    "",
    `Reported By:\n${pendingBug.reportedBy?.trim() || "—"}`,
    "",
    `Severity:\n${formatSeverityLabel(pendingBug.severity)}`,
    "",
    `Priority:\n${pendingBug.priority?.trim() || "normal"}`,
    "",
    `Tags:\n${pendingBug.tags?.trim() || "—"}`,
    "",
    "---",
    "Debug capture",
    `- Capture ID: ${manifest.id}`,
    `- URL: ${manifest.url}`,
    `- Path: ${manifest.pathname}`,
    ...(manifest.pageEntity
      ? [
          `- Record: ${manifest.pageEntity.type} (${manifest.pageEntity.id})`,
        ]
      : []),
    ...(manifest.surface
      ? [
          `- Modal: ${manifest.surface.title ?? "—"} (${manifest.surface.mode ?? "—"})`,
          `- Modal step: ${formatModalStep(manifest.surface) ?? "—"}`,
          ...(manifest.surface.entityId
            ? [
                `- Modal record: ${manifest.surface.entityType ?? "Entity"} (${manifest.surface.entityId})`,
              ]
            : []),
          ...(manifest.surface.itemLabel
            ? [`- Modal item: ${manifest.surface.itemLabel}`]
            : []),
        ]
      : []),
    `- Type: ${manifest.captureType}`,
    `- Target: ${manifest.target.humanLabel ?? manifest.target.selector}${manifest.target.testId ? ` [data-testid=${manifest.target.testId}]` : ""}`,
    `- Selector: ${manifest.target.selector}`,
    `- React stack: ${stack || "n/a"}`,
    `- Primary source: ${primaryHint}`,
    `- Bundle: ${getCaptureZipFilename(manifest.id)} (attach to your AI agent chat)`,
    ...(manifest.failedRequests?.length
      ? [
          "",
          "Failed API calls (most recent first):",
          ...manifest.failedRequests.map((request, index) => {
            const payload =
              request.requestPayload !== undefined
                ? JSON.stringify(request.requestPayload, null, 2)
                : "n/a";
            const response =
              request.responseBody !== undefined
                ? JSON.stringify(request.responseBody, null, 2)
                : "n/a";

            return [
              `${index + 1}. ${request.method} ${request.url}`,
              `   Status: ${request.status ?? "n/a"} ${request.statusText ?? ""}`.trim(),
              `   Payload: ${payload}`,
              `   Response: ${response}`,
            ].join("\n");
          }),
        ]
      : []),
  ].join("\n");
}
